import React, { useState, useRef, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";

const BaseTooltip = ({
  text,
  label,
  color = "var(--brand-indigo)",
  disabled,
  position = "top",
  maxWidth = 280,
  children,
}) => {
  const content = label ?? text;
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);
  const timerRef = useRef(null);

  const OFFSET = 8;

  // Calculate fixed-position coords — no scroll offset needed with position:fixed
  const calcPosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();

    let top, left;
    switch (position) {
      case "bottom":
        top = rect.bottom + OFFSET;
        left = rect.left + rect.width / 2;
        break;
      case "left":
        top = rect.top + rect.height / 2;
        left = rect.left - OFFSET;
        break;
      case "right":
        top = rect.top + rect.height / 2;
        left = rect.right + OFFSET;
        break;
      default: // top
        top = rect.top - OFFSET;
        left = rect.left + rect.width / 2;
    }

    if (position === "top" || position === "bottom") {
      const tooltipWidth = tooltipRef.current ? tooltipRef.current.offsetWidth : 120;
      const minLeft = tooltipWidth / 2 + 10;
      const maxLeft = window.innerWidth - tooltipWidth / 2 - 10;
      left = Math.max(minLeft, Math.min(maxLeft, left));
    }

    setCoords({ top, left });
  }, [position]);

  const show = () => {
    calcPosition();
    timerRef.current = setTimeout(() => setVisible(true), 60);
  };
  const hide = () => {
    clearTimeout(timerRef.current);
    setVisible(false);
  };

  useEffect(() => {
    if (!visible) return;
    const handle = requestAnimationFrame(calcPosition);
    window.addEventListener("scroll", calcPosition, true);
    window.addEventListener("resize", calcPosition);
    return () => {
      cancelAnimationFrame(handle);
      window.removeEventListener("scroll", calcPosition, true);
      window.removeEventListener("resize", calcPosition);
    };
  }, [visible, calcPosition]);

  if (disabled || !content) return children || null;

  // CSS transform to correctly place tooltip relative to the anchor point
  const transformMap = {
    top: "translateX(-50%) translateY(-100%)",
    bottom: "translateX(-50%)",
    left: "translateX(-100%) translateY(-50%)",
    right: "translateY(-50%)",
  };

  // Origin point for scale animation — keeps tooltip growing from the anchor
  const originMap = {
    top: "bottom center",
    bottom: "top center",
    left: "center right",
    right: "center left",
  };

  const arrowBase = {
    position: "absolute",
    width: 8,
    height: 8,
    background: "var(--dropdown-bg)",
    border: "1px solid var(--dropdown-border)",
  };
  const arrowMap = {
    top: {
      bottom: -5,
      left: "50%",
      transform: "translateX(-50%) rotate(45deg)",
      borderTop: "none",
      borderLeft: "none",
    },
    bottom: {
      top: -5,
      left: "50%",
      transform: "translateX(-50%) rotate(45deg)",
      borderBottom: "none",
      borderRight: "none",
    },
    left: {
      right: -5,
      top: "50%",
      transform: "translateY(-50%) rotate(45deg)",
      borderLeft: "none",
      borderBottom: "none",
    },
    right: {
      left: -5,
      top: "50%",
      transform: "translateY(-50%) rotate(45deg)",
      borderRight: "none",
      borderTop: "none",
    },
  };

  const tooltip = visible
    ? ReactDOM.createPortal(
        <div
          ref={tooltipRef}
          role="tooltip"
          style={{
            position: "fixed", // fixed so no scroll offset needed
            top: coords.top,
            left: coords.left,
            transform: transformMap[position],
            transformOrigin: originMap[position],
            zIndex: 99999,
            maxWidth,
            width: "max-content",
            pointerEvents: "none",
            // Inline animation so it doesn't fight with the transform above
            animation: "tooltipFadeIn 0.12s ease both",
          }}
        >
          {/* Inline keyframe — scoped, no conflict with global styles */}
          <style>{`
        @keyframes tooltipFadeIn {
          from { opacity: 0; transform: ${transformMap[position]} scale(0.9); }
          to   { opacity: 1; transform: ${transformMap[position]} scale(1); }
        }
      `}</style>

          <div
            style={{
              background: "var(--dropdown-bg)",
              border: "1px solid var(--dropdown-border)",
              borderLeft: `3px solid ${color}`,
              borderRadius: 10,
              padding: "7px 12px",
              boxShadow: "0 12px 40px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.04)",
              backdropFilter: "blur(16px)",
              position: "relative",
              whiteSpace: maxWidth > 100 ? "normal" : "nowrap",
              width: "100%",
              maxWidth: "100%",
            }}
          >
            <span
              style={{
                display: "block",
                fontSize: "0.75rem",
                fontWeight: 500,
                color: "var(--text-primary)",
                lineHeight: 1.5,
                wordBreak: "break-word",
                whiteSpace: maxWidth > 100 ? "normal" : "nowrap",
                letterSpacing: "0.01em",
              }}
            >
              {content}
            </span>
            <span style={{ ...arrowBase, ...arrowMap[position] }} />
          </div>
        </div>,
        document.body,
      )
    : null;

  return (
    <>
      <span
        ref={triggerRef}
        style={{ position: "relative", display: "inline-flex" }}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
      >
        {children}
      </span>
      {tooltip}
    </>
  );
};

export default BaseTooltip;
